import { makeAutoObservable, runInAction } from 'mobx'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { uiStore } from 'shared/store/uiStore'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { BillingPath } from 'entities/ExportRouters'

type ITrialLimitModalType = 'numbers' | 'inboxes' | 'members'

export class TrialLimitModalStore {
  private _modalId = 'trialLimit_modal'
  private _loading = true
  private _type: ITrialLimitModalType | null = null

  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)
  }

  get trialSubscription() {
    return this._entities.subscriptionStore.trialSubscriptionStore
  }

  get loading() {
    return this._loading
  }

  init = async () => {
    if (!this._entities.subscriptionStore.isTrial) {
      runInAction(() => {
        this._loading = false
      })

      return
    }

    runInAction(() => {
      this._loading = true
    })

    await this.trialSubscription.init()

    runInAction(() => {
      this._loading = false
    })
  }

  get title() {
    const entityText = this._getEntityText

    if (this._entities.organizationStore.isTwilioNumberVendor) {
      return this.isAdminOrOwnerRole
        ? `Upgrade to add ${entityText}`
        : `To add ${entityText}, please ask one of your admins to upgrade`
    }

    if (this.isAdminOrOwnerRole) {
      if (this.isCompleted) {
        return `Upgrade to add ${entityText}`
      }

      return `Get approved and upgrade to add ${entityText}`
    }

    if (this.isCompleted) {
      return `To add ${entityText}, please ask one of your admins to upgrade`
    }

    return `To add ${entityText}, please ask one of your admins to get approved and upgrade`
  }

  private get _getEntityText() {
    if (this._type === 'inboxes') {
      return 'an inbox'
    }
    if (this._type === 'members') {
      return 'a member'
    }
    return 'a number'
  }

  get action() {
    if (this.isCompleted || this._entities.organizationStore.isTwilioNumberVendor) {
      return {
        text: 'Upgrade plan',
        onAction: () => {
          uiStore.changeRouteViaVue({
            path: `/${BillingPath.upgrade}`,
          })
          this.onClose()
        },
      }
    }

    return {
      text: 'Get approved',
      onAction: () => {
        uiStore.changeRouteViaVue({
          path: '/settings/organization/compliance',
        })
        this.onClose()
      },
    }
  }

  open = async (type: ITrialLimitModalType) => {
    this._type = type
    this.init()

    if (!this._entities.subscriptionStore.isTrial) return

    if (this.isAdminOrOwnerRole) {
      modalStore.addModal({
        id: this._modalId,
        type: ModalTypeList.INFO,
        title: this.title,
        primaryAction: this.action,
        secondaryAction: {
          text: 'Cancel',
          onAction: () => {
            this.onClose()
          },
        },
      })
    } else {
      modalStore.addModal({
        id: this._modalId,
        type: ModalTypeList.INFO,
        title: this.title,
        infoModalTextAction: 'Done',
      })
    }
  }

  onClose = () => {
    modalStore.closeModal(this._modalId)
  }

  get isCompleted() {
    return this.trialSubscription.isStatusCompleted
  }

  get isAdminOrOwnerRole() {
    return this._entities.usersStore.user?.isAdminOrOwnerRole
  }
}
